/**
 * What a right-click on a call in the list offers.
 *
 * The entries are the questions asked while working down a call list, in the
 * order they get asked: open it, ring them back, take the number somewhere else,
 * look at their site, find the rest of the calls to this number, file it under a
 * label. Nothing here needs the call's detail to have been fetched - every field
 * used is already on the list row, so the menu is instant on a row that has
 * never been opened.
 *
 * Deliberately absent: delete. The call pane's own menu leaves it out too, and
 * for the same reason - a delete that leaves the recording paying rent in object
 * storage is worse than no delete at all.
 */
import { Link } from "react-router-dom";
import {
  Ban,
  CheckCheck,
  ExternalLink,
  Filter,
  Hash,
  LifeBuoy,
  Phone,
  PhoneOff,
  PhoneOutgoing,
  Undo2,
} from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { callCounterpart, isCallback, type CallRow } from "../../../lib/calls";
import type { CrmLabel } from "../../../lib/crmLabels";
import { formatPhone, normalizeE164 } from "../../../lib/phone";
import { useSuppressedNumbers } from "../../telephony/useSuppressedNumbers";
import { LabelItems, fmtStamp, useCopyToast } from "../../ui";

export function CallRowItems({
  call,
  labels,
  canCall,
  close,
  onOpen,
  onCallBack,
  onSearch,
  onSuppress,
  onBlock,
  blocked,
  onPatch,
  onHandled,
}: {
  call: CallRow;
  labels: CrmLabel[];
  /** Click-to-call is permission-gated and needs telephony configured. */
  canCall: boolean;
  close: () => void;
  onOpen: (id: string) => void;
  onCallBack: (call: CallRow) => void;
  /** Put a term in the list's search box - "every call to this number". */
  onSearch: (term: string) => void;
  /** Ask to put this number on the do-not-call list; the section confirms it. */
  onSuppress: (call: CallRow) => void;
  /** Ask to refuse this caller in future; the section confirms it. */
  onBlock: (call: CallRow) => void;
  /** Numbers blocked from this list this session. Nothing in the shell holds
   *  the blocklist, so the set has to be passed in. */
  blocked: ReadonlySet<string>;
  onPatch: (id: string, body: { tags: string[] }) => Promise<void>;
  /** Tick a waiting call off, or put it back. Only offered on the rows the
   *  queue actually holds - see `isCallback`. */
  onHandled: (call: CallRow, handled: boolean) => void;
}) {
  const t = useT();
  const copy = useCopyToast();
  const suppressed = useSuppressedNumbers();
  // The other party, the same way the row itself reads it: an inbound row keeps
  // our own Twilio line in `toNumber`, so every entry below that means "them"
  // has to come from here.
  const other = callCounterpart(call);
  const marked = !!suppressed?.has(normalizeE164(other));

  const act = (fn: () => void) => {
    close();
    fn();
  };

  return (
    <>
      {/* Which row this is acting on. A list of two hundred calls and a menu at
          the pointer is a combination where "the one I right-clicked" is worth
          spelling out. */}
      <div className="crm-menu-head">
        <div className="crm-menu-t">
          {call.companyName ?? (other ? formatPhone(other) : t("calls.incoming.withheld"))}
        </div>
        {/* The number only when the title is not already it. A third of these
            calls have no company name, and printing the number twice, one line
            under itself, reads as a rendering fault. */}
        <div className="crm-menu-s">
          {call.companyName && other ? `${formatPhone(other)} · ` : ""}
          {fmtStamp(call.startedAt)}
        </div>
      </div>

      <button type="button" className="crm-mi" onClick={() => act(() => onOpen(call.id))}>
        <ExternalLink className="size-4" />
        {t("calls.row.open")}
      </button>

      {/* Straight to the support thread on a message that has already been
          filed - the reason this is here rather than only in the pane is that
          the folder is worked from the list, and opening a call to reach its
          ticket is two clicks to get to the second one. */}
      {call.ticket ? (
        <Link className="crm-mi" to={`/support/${call.ticket.id}`} onClick={close}>
          <LifeBuoy className="size-4" />
          {t("calls.detail.openTicket", { number: call.ticket.number })}
        </Link>
      ) : null}

      {canCall && other ? (
        <button type="button" className="crm-mi" onClick={() => act(() => onCallBack(call))}>
          <PhoneOutgoing className="size-4" />
          {t("calls.detail.callBack")}
        </button>
      ) : null}

      {/* Directly under "Call back", because these are the two halves of the
          same act and the second one is what empties the folder. Offered only on
          rows the queue holds: an entry on an answered outbound call would be a
          button that reports success and changes nothing visible. */}
      {isCallback(call) ? (
        <button
          type="button"
          className="crm-mi"
          onClick={() => act(() => onHandled(call, !call.handledAt))}
        >
          {call.handledAt ? <Undo2 className="size-4" /> : <CheckCheck className="size-4" />}
          {t(call.handledAt ? "calls.callback.undo" : "calls.callback.done")}
        </button>
      ) : null}

      {other ? (
        <>
          <button type="button" className="crm-mi" onClick={() => act(() => copy(other))}>
            <Phone className="size-4" />
            {t("calls.detail.copyNumber")}
          </button>

          <button type="button" className="crm-mi" onClick={() => act(() => onSearch(other))}>
            <Filter className="size-4" />
            {t("calls.row.findNumber")}
          </button>

          {/* Confirmed by the section rather than here: this menu unmounts on
              the click, and a dialog rendered inside it would go with it. */}
          {!marked ? (
            <button type="button" className="crm-mi" onClick={() => act(() => onSuppress(call))}>
              <PhoneOff className="size-4" />
              {t("calls.suppression.mark")}
            </button>
          ) : null}

          {/* INBOUND ONLY, and that is a rule rather than caution: the list is
              read by one guard, on the webhook that answers calls coming IN.
              Blocking a number we rang would write a row that changes nothing
              anybody can see.

              This is the way in that matters. A sales robot is recognised from
              the LIST - three rows from the same unknown number in one morning -
              rather than from any one call, and making somebody open a call to
              refuse it is how the list stops getting used. */}
          {call.direction === "inbound" && !blocked.has(normalizeE164(other) ?? "") ? (
            <button type="button" className="crm-mi" onClick={() => act(() => onBlock(call))}>
              <Ban className="size-4" />
              {t("calls.blocklist.mark")}
            </button>
          ) : null}
        </>
      ) : null}

      {call.leadDomain ? (
        <>
          <a
            className="crm-mi"
            href={`https://${call.leadDomain}`}
            target="_blank"
            rel="noreferrer noopener"
            onClick={close}
          >
            <ExternalLink className="size-4" />
            {t("calls.row.openSite")}
          </a>
          <button type="button" className="crm-mi" onClick={() => act(() => copy(call.leadDomain!))}>
            <Hash className="size-4" />
            {t("leads.copyDomain")}
          </button>
        </>
      ) : null}

      <button type="button" className="crm-mi" onClick={() => act(() => copy(call.id))}>
        <Hash className="size-4" />
        {t("calls.detail.copyId")}
      </button>

      {/* The menu stays open through a label click: two labels on one call is
          the normal case, and closing after each would mean reopening. */}
      <LabelItems
        title={t("calls.pane.labels")}
        labels={labels}
        tags={call.tags ?? []}
        onApply={(next) => onPatch(call.id, { tags: next })}
      />
    </>
  );
}
